import { AppShell, FullWidthPage, useMediaQuery, useTheme } from "@wayflyer/flyui";
import {
  PanelContainer,
  PanelPadding,
  ChartFilterSwitches,
  DatePickerHeader,
  ReferencedMultiLineBarChart,
  TemporalResolutionPickers,
  type ValueType,
} from "@wayflyer/flyui-dataviz";

import { ChartTopRow, DataGroupContainer } from "../../components/styled";
import { INTERNAL_REV_METRIC } from "../internal-revenue";
import { SummaryPanel } from "../../components/summary-panel";

export interface RevenueScreenProps {
  pageTitle: string;
  chartData;
  datePickerHeaderProps;
  hint?: string;
  groups: string[];
  groupMapping;
  formatValue: (rawValue: ValueType, isRef?: boolean) => string;
  chartFilters;
  thisYearEnabled: boolean;
  lastYearEnabled: boolean;
  summaryData;
  onSelectSummaryTab;
  percentageChartData;
  formatPercentageValue;
}

/**
 * InternalRevenueScreen component
 */
export const InternalRevenueScreen = ({
  pageTitle,
  chartData,
  datePickerHeaderProps,
  hint = "",
  groups,
  groupMapping,
  formatValue,
  chartFilters,
  thisYearEnabled,
  lastYearEnabled,
  summaryData,
  onSelectSummaryTab,
  percentageChartData,
  formatPercentageValue,
}: RevenueScreenProps) => {
  const query = useMediaQuery();
  const theme = useTheme();

  const lines = groups.map((group) => {
    return {
      datumName: group + INTERNAL_REV_METRIC,
      name: group + " This Year",
      referenceName: group + " Last Year",
      colorId: groupMapping[group],
    };
  });

  const bars = groups
    .filter((g) => g !== "Total")
    .map((group) => {
      return {
        datumName: group + INTERNAL_REV_METRIC,
        name: group + " This Year",
        referenceName: group + " Last Year",
        colorId: ((groupMapping[group] * 3 + 1) % 6) as any,
        stackId: "stack",
      };
    });

  const { onClickDateRes, hideMonthly, hideWeekly, selectedButtonIndex, ...rest } = chartFilters;
  const temporalResPickerProps = { onClickDateRes, hideWeekly, hideMonthly, selectedButtonIndex };
  const chartFilterSwitchesProps = {
    switchOneColor: theme.palette.bg.accent.strong,
    switchTwoColor: theme.palette.bg.accent.strong,
    ...rest,
    switchOneOn: thisYearEnabled,
    switchTwoOn: lastYearEnabled,
  };

  return (
    <AppShell>
      <FullWidthPage title={pageTitle}>
        <DataGroupContainer>
          <DatePickerHeader
            summary={query("sm").windowGt && hint ? hint : ""}
            onChange={datePickerHeaderProps.onChange}
            currentPreset={datePickerHeaderProps.currentPreset}
            dateRangePresets={datePickerHeaderProps.dateRangePresets}
            startDate={datePickerHeaderProps.startDate}
            endDate={datePickerHeaderProps.endDate}
          />
          <SummaryPanel
            summaryItems={summaryData}
            metricReferenceText="vs. last year"
            loading={false}
            onSelect={onSelectSummaryTab}
            useAnalytics={false}
          />
          <PanelContainer>
            <PanelPadding>
              <ChartTopRow>
                {temporalResPickerProps ? (
                  <TemporalResolutionPickers {...temporalResPickerProps} className="no-padding" />
                ) : null}
                {chartFilterSwitchesProps ? <ChartFilterSwitches {...chartFilterSwitchesProps} /> : null}
              </ChartTopRow>
              <ReferencedMultiLineBarChart
                data={chartData}
                lines={lines}
                formatValueBar={formatValue}
                formatValueLine={formatValue}
                showLegend={true}
                currentRangeEnabled={thisYearEnabled}
                referenceRangeEnabled={lastYearEnabled}
              />
            </PanelPadding>
          </PanelContainer>
          <PanelContainer>
            <PanelPadding>
              <ChartTopRow>
                <ReferencedMultiLineBarChart
                  data={percentageChartData}
                  bars={bars}
                  formatValueBar={formatPercentageValue}
                  formatValueLine={formatPercentageValue}
                  showLegend={true}
                  currentRangeEnabled={true}
                  referenceRangeEnabled={false}
                />
              </ChartTopRow>
            </PanelPadding>
          </PanelContainer>
        </DataGroupContainer>
      </FullWidthPage>
    </AppShell>
  );
};
